import React, { useEffect, useRef, useState } from 'react';
import { Calendar, ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Info, X } from 'lucide-react';
import LoadingIndicator from './LoadingIndicator';
import { useDisplayCurrency } from '../context/DisplayCurrencyContext';
import { getApiUrl } from '../utils/config';
import {
  formatUsdAmount,
  getUsdPerSavingsToken,
  parseSavingsTokenAmount,
  readXrpUsdRateFromExchangePayload,
} from '../utils/displayCurrencyFormat';
import './AddSavingsPlanModal.css';

const XRP_IMG =
  'https://assets.coingecko.com/coins/images/44/small/xrp-symbol-white-128.png?1605778731';
const USDT_IMG = 'https://assets.coingecko.com/coins/images/325/small/Tether-logo.png';
const USDC_IMG =
  'https://assets.coingecko.com/coins/images/6319/small/USD_Coin_icon.png?1547042389';

const DEFAULT_PLANS = ['Fixed', 'Flex Savings', 'Auto Savings', 'Goal'];

const DEFAULT_TOKENS = [
  { id: 'XRP', label: 'XRP wallet', ticker: 'XRP', iconUrl: XRP_IMG },
  { id: 'USDT', label: 'USDT wallet', ticker: 'USDT', iconUrl: USDT_IMG },
  { id: 'USDC', label: 'USDC wallet', ticker: 'USDC', iconUrl: USDC_IMG },
];

function formatTokenQuantity(amount) {
  return Number(amount).toLocaleString('en-US', { maximumFractionDigits: 6 });
}

function formatSavingsExchangeLine({
  amount,
  ticker,
  usdPerToken,
  formatValue,
  isLoading,
  fallbackLine,
}) {
  if (isLoading && !(usdPerToken > 0)) return 'Loading exchange rate…';
  if (!(usdPerToken > 0)) return fallbackLine || '';
  const entered = parseSavingsTokenAmount(amount);
  if (entered != null && entered > 0) {
    return `${formatTokenQuantity(entered)} ${ticker} ≈ ${formatValue(entered * usdPerToken)}`;
  }
  return `1 ${ticker} = ${formatValue(usdPerToken)}`;
}

function ExchangeRateLine({ line, emphasizeValue = false, className = '' }) {
  if (!line) return null;
  const match = emphasizeValue ? String(line).match(/^(.*?\s[=≈]\s*)(.+)$/) : null;
  return (
    <p className={`savings-asp-rate-line${className ? ` ${className}` : ''}`}>
      {match ? (
        <>
          {match[1]}
          <span className="savings-asp-rate-value">{match[2]}</span>
        </>
      ) : (
        line
      )}
    </p>
  );
}

/** Plans that collect an upfront goal amount (XRP → USD via exchange line). */
export function planRequiresGoalAmount(plan) {
  return plan === 'Fixed' || plan === 'Auto Savings';
}

export function planIsAutoSavings(plan) {
  return plan === 'Auto Savings';
}

export function planRequiresTargetDate(plan) {
  return plan === 'Fixed';
}

const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function parseIsoDate(value) {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

function toIsoDate(date) {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function startOfLocalDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function shiftMonth(year, month, delta) {
  const next = new Date(year, month + delta, 1);
  return { year: next.getFullYear(), month: next.getMonth() };
}

function monthIndex(year, month) {
  return year * 12 + month;
}

function buildMonthCells(year, month) {
  const leading = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = Array.from({ length: leading }, () => null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(new Date(year, month, day));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function formatTargetDateLabel(iso) {
  const date = parseIsoDate(iso);
  if (!date) return '';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function SavingsTargetDatePicker({ id, value, onChange, disabled, closeTick, onOpen }) {
  const minDate = startOfLocalDay(new Date());
  const maxDate = new Date(minDate.getFullYear() + 30, minDate.getMonth(), minDate.getDate());
  const minMonth = monthIndex(minDate.getFullYear(), minDate.getMonth());
  const maxMonth = monthIndex(maxDate.getFullYear(), maxDate.getMonth());
  const selected = parseIsoDate(value);
  const selectedIso = selected ? toIsoDate(selected) : '';
  const todayIso = toIsoDate(minDate);
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(() => {
    const base = selected || minDate;
    return { year: base.getFullYear(), month: base.getMonth() };
  });
  const wrapRef = useRef(null);
  const calendarRef = useRef(null);

  useEffect(() => {
    setOpen(false);
  }, [closeTick]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close, { passive: true });
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    calendarRef.current?.scrollIntoView({ block: 'nearest' });
  }, [open, cursor.year, cursor.month]);

  const cursorKey = monthIndex(cursor.year, cursor.month);
  const title = new Date(cursor.year, cursor.month, 1).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });
  const label = formatTargetDateLabel(selectedIso);

  const moveCursor = (delta) => {
    const next = shiftMonth(cursor.year, cursor.month, delta);
    const nextKey = monthIndex(next.year, next.month);
    if (nextKey < minMonth) {
      setCursor({ year: minDate.getFullYear(), month: minDate.getMonth() });
      return;
    }
    if (nextKey > maxMonth) {
      setCursor({ year: maxDate.getFullYear(), month: maxDate.getMonth() });
      return;
    }
    setCursor(next);
  };

  const openPicker = () => {
    if (disabled) return;
    const base = selected || minDate;
    setCursor({ year: base.getFullYear(), month: base.getMonth() });
    setOpen(true);
    onOpen?.();
  };

  return (
    <div className="savings-asp-date-wrap" ref={wrapRef}>
      <button
        id={id}
        type="button"
        className={`savings-asp-trigger savings-asp-date-trigger ${open ? 'is-open' : ''}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={open ? `${id}-dialog` : undefined}
        onClick={() => (open ? setOpen(false) : openPicker())}
        disabled={disabled}
      >
        <span className={`savings-asp-date-value ${label ? '' : 'is-placeholder'}`}>
          <Calendar size={16} strokeWidth={2} aria-hidden />
          {label || 'Select date'}
        </span>
        <ChevronDown size={18} strokeWidth={2} className="savings-asp-trigger-chevron" aria-hidden />
      </button>
      {open ? (
        <div
          id={`${id}-dialog`}
          className="savings-asp-calendar"
          role="dialog"
          aria-label="Choose target date"
          ref={calendarRef}
        >
          <div className="savings-asp-calendar-head">
            <div className="savings-asp-calendar-nav-group">
              <button
                type="button"
                className="savings-asp-calendar-nav"
                aria-label="Previous year"
                disabled={cursorKey <= minMonth}
                onClick={() => moveCursor(-12)}
              >
                <ChevronsLeft size={16} strokeWidth={2} />
              </button>
              <button
                type="button"
                className="savings-asp-calendar-nav"
                aria-label="Previous month"
                disabled={cursorKey <= minMonth}
                onClick={() => moveCursor(-1)}
              >
                <ChevronLeft size={16} strokeWidth={2} />
              </button>
            </div>
            <p className="savings-asp-calendar-title">{title}</p>
            <div className="savings-asp-calendar-nav-group">
              <button
                type="button"
                className="savings-asp-calendar-nav"
                aria-label="Next month"
                disabled={cursorKey >= maxMonth}
                onClick={() => moveCursor(1)}
              >
                <ChevronRight size={16} strokeWidth={2} />
              </button>
              <button
                type="button"
                className="savings-asp-calendar-nav"
                aria-label="Next year"
                disabled={cursorKey >= maxMonth}
                onClick={() => moveCursor(12)}
              >
                <ChevronsRight size={16} strokeWidth={2} />
              </button>
            </div>
          </div>
          <div className="savings-asp-calendar-weekdays" aria-hidden="true">
            {WEEKDAY_LABELS.map((day) => (
              <span key={day} className="savings-asp-calendar-weekday">
                {day}
              </span>
            ))}
          </div>
          <div className="savings-asp-calendar-grid">
            {buildMonthCells(cursor.year, cursor.month).map((date, index) => {
              if (!date) {
                return <span key={`empty-${index}`} className="savings-asp-calendar-day is-empty" />;
              }
              const iso = toIsoDate(date);
              const isSelected = iso === selectedIso;
              const isToday = iso === todayIso;
              return (
                <button
                  key={iso}
                  type="button"
                  className={`savings-asp-calendar-day${isSelected ? ' is-selected' : ''}${isToday ? ' is-today' : ''}`}
                  disabled={date < minDate || date > maxDate}
                  aria-pressed={isSelected}
                  aria-label={date.toLocaleDateString(undefined, {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                  onClick={() => {
                    onChange(iso);
                    setOpen(false);
                  }}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            className="savings-asp-calendar-today"
            onClick={() => {
              onChange(todayIso);
              setCursor({ year: minDate.getFullYear(), month: minDate.getMonth() });
              setOpen(false);
            }}
          >
            Today
          </button>
        </div>
      ) : null}
    </div>
  );
}

const DEFAULT_AUTOSAVE_FREQUENCIES = ['Daily', 'Weekly', 'Bi-weekly', 'Monthly'];

const DEFAULT_NOTE_FIXED =
  "In fixed savings once created money can't be withdrawn till financial goal is reached";

const DEFAULT_NOTE_FLEX_GOAL =
  'You can add money anytime after creating this plan. Contributions are flexible—no upfront goal amount is required.';

/**
 * Add savings plan modal (slide-up sheet + desktop dialog).
 */
const AddSavingsPlanModal = ({
  isOpen,
  onClose,
  name,
  onNameChange,
  planOptions = DEFAULT_PLANS,
  selectedPlan,
  onSelectPlan,
  amount,
  onAmountChange,
  walletLabel = 'XRP wallet',
  walletTicker = 'XRP',
  useCoinIconInPill = true,
  tokenOptions = DEFAULT_TOKENS,
  selectedToken,
  onSelectToken,
  exchangeRateLine = '',
  onCreate,
  isSubmitting = false,
  /** Overrides automatic footnote (fixed/auto vs flex/goal). */
  footnote,
  autoSaveAmount = '',
  onAutoSaveAmountChange = () => {},
  autoSaveFrequency = '',
  onAutoSaveFrequencyChange = () => {},
  autosaveFrequencyOptions = DEFAULT_AUTOSAVE_FREQUENCIES,
  targetDate = '',
  onTargetDateChange = () => {},
}) => {
  const [planMenuOpen, setPlanMenuOpen] = useState(false);
  const [freqMenuOpen, setFreqMenuOpen] = useState(false);
  const [tokenMenuOpen, setTokenMenuOpen] = useState(false);
  const [dateCloseTick, setDateCloseTick] = useState(0);
  const [internalTokenId, setInternalTokenId] = useState(selectedToken || walletTicker || 'XRP');
  const [fallbackXrpUsdRate, setFallbackXrpUsdRate] = useState(null);
  const [fallbackRates, setFallbackRates] = useState(null);
  const [isFetchingRate, setIsFetchingRate] = useState(false);
  const planWrapRef = useRef(null);
  const freqWrapRef = useRef(null);
  const tokenWrapRef = useRef(null);

  const tokens = tokenOptions.length > 0 ? tokenOptions : DEFAULT_TOKENS;
  const activeTokenId = selectedToken || internalTokenId;
  const activeToken =
    tokens.find((token) => token.id === activeTokenId || token.ticker === activeTokenId) || tokens[0];
  const activeTicker = activeToken?.ticker || walletTicker;
  const activeLabel = activeToken?.label || walletLabel;
  const activeIconUrl = activeToken?.iconUrl || (String(activeTicker).toUpperCase() === 'XRP' ? XRP_IMG : '');
  const showTokenIcon = useCoinIconInPill && Boolean(activeIconUrl);
  const {
    displayCurrency,
    exchangeRates,
    xrpUsdRate,
    exchangeQuoteDirection,
    isLoadingExchangeRates,
    formatFromUsd,
  } = useDisplayCurrency();

  useEffect(() => {
    if (!isOpen || xrpUsdRate != null || isLoadingExchangeRates) return undefined;
    let cancelled = false;
    setIsFetchingRate(true);
    fetch(getApiUrl('api/exchange/rates'))
      .then((response) => (response.ok ? response.json() : null))
      .then((result) => {
        if (cancelled || !result) return;
        setFallbackXrpUsdRate(readXrpUsdRateFromExchangePayload(result));
        if (Array.isArray(result?.data?.rates)) setFallbackRates(result.data.rates);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setIsFetchingRate(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, xrpUsdRate, isLoadingExchangeRates]);

  const usdPerToken = getUsdPerSavingsToken(
    activeTicker,
    exchangeRates?.length ? exchangeRates : fallbackRates,
    exchangeQuoteDirection,
    xrpUsdRate ?? fallbackXrpUsdRate,
  );
  const formatExchangeValue = (usd) => {
    const display = String(displayCurrency || 'USD').toUpperCase();
    const token = String(activeTicker || '').toUpperCase();
    if (display === 'USD' || display === token) return formatUsdAmount(usd);
    return formatFromUsd(usd);
  };
  const goalExchangeLine = formatSavingsExchangeLine({
    amount,
    ticker: activeTicker,
    usdPerToken,
    formatValue: formatExchangeValue,
    isLoading: isLoadingExchangeRates || isFetchingRate,
    fallbackLine: exchangeRateLine,
  });
  const autoSaveExchangeLine = formatSavingsExchangeLine({
    amount: autoSaveAmount,
    ticker: activeTicker,
    usdPerToken,
    formatValue: formatExchangeValue,
    isLoading: isLoadingExchangeRates || isFetchingRate,
    fallbackLine: '',
  });

  useEffect(() => {
    if (!isOpen) {
      setPlanMenuOpen(false);
      setFreqMenuOpen(false);
      setTokenMenuOpen(false);
      if (!selectedToken) setInternalTokenId(walletTicker || 'XRP');
    }
  }, [isOpen, selectedToken, walletTicker]);

  useEffect(() => {
    if (!planMenuOpen) return undefined;
    const close = (e) => {
      if (planWrapRef.current && !planWrapRef.current.contains(e.target)) {
        setPlanMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close, { passive: true });
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
    };
  }, [planMenuOpen]);

  useEffect(() => {
    if (!freqMenuOpen) return undefined;
    const close = (e) => {
      if (freqWrapRef.current && !freqWrapRef.current.contains(e.target)) {
        setFreqMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close, { passive: true });
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
    };
  }, [freqMenuOpen]);

  useEffect(() => {
    if (!tokenMenuOpen) return undefined;
    const close = (e) => {
      if (tokenWrapRef.current && !tokenWrapRef.current.contains(e.target)) {
        setTokenMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close, { passive: true });
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
    };
  }, [tokenMenuOpen]);

  const onAmountRowInput = (e) => {
    let v = e.target.value.replace(/[^0-9.,]/g, '');
    onAmountChange(v);
  };

  const resolvedPlan =
    (selectedPlan && planOptions.includes(selectedPlan) ? selectedPlan : null) || planOptions[0] || 'Fixed';
  const showGoalAmount = planRequiresGoalAmount(resolvedPlan);
  const showTargetDate = planRequiresTargetDate(resolvedPlan);
  const showAutoFields = planIsAutoSavings(resolvedPlan);
  const infoNote =
    footnote ?? (showGoalAmount ? DEFAULT_NOTE_FIXED : DEFAULT_NOTE_FLEX_GOAL);

  useEffect(() => {
    if (!isOpen || showGoalAmount) return;
    onAmountChange('');
  }, [isOpen, showGoalAmount, onAmountChange]);

  useEffect(() => {
    if (!isOpen || showAutoFields) return;
    onAutoSaveAmountChange('');
    onAutoSaveFrequencyChange('');
  }, [isOpen, showAutoFields, onAutoSaveAmountChange, onAutoSaveFrequencyChange]);

  const onAutosaveAmountInput = (e) => {
    let v = e.target.value.replace(/[^0-9.,]/g, '');
    onAutoSaveAmountChange(v);
  };

  if (!isOpen) return null;

  const titleId = 'add-savings-plans-modal-title';

  return (
    <div className="savings-asp-overlay" onClick={onClose} role="presentation">
      <div
        className="savings-asp-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="savings-asp-head">
          <h2 id={titleId} className="savings-asp-title">
            Add Savings Plans
          </h2>
          <button type="button" className="savings-asp-close" onClick={onClose} aria-label="Close">
            <X size={22} strokeWidth={2} />
          </button>
        </header>

        <div className="savings-asp-field">
          <label className="savings-asp-label" htmlFor="savings-asp-name-input">
            Name
          </label>
          <input
            id="savings-asp-name-input"
            type="text"
            className="savings-asp-input"
            placeholder="Enter name"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            disabled={isSubmitting}
            autoComplete="off"
          />
        </div>

        <div className="savings-asp-field">
          <span className="savings-asp-label" id="savings-asp-plan-label">
            Plan
          </span>
          <div className="savings-asp-menu-wrap" ref={planWrapRef}>
            <button
              type="button"
              className={`savings-asp-trigger ${planMenuOpen ? 'is-open' : ''}`}
              aria-expanded={planMenuOpen}
              aria-haspopup="listbox"
              aria-labelledby="savings-asp-plan-label"
                  onClick={() => {
                    setPlanMenuOpen((o) => !o);
                    setFreqMenuOpen(false);
                    setTokenMenuOpen(false);
                    setDateCloseTick((tick) => tick + 1);
                  }}
              disabled={isSubmitting}
            >
              <span className="savings-asp-trigger-value">{resolvedPlan}</span>
              <ChevronDown size={18} strokeWidth={2} className="savings-asp-trigger-chevron" aria-hidden />
            </button>
            {planMenuOpen ? (
              <ul className="savings-asp-plan-menu" role="listbox">
                {planOptions.map((opt) => (
                  <li key={opt}>
                    <button
                      type="button"
                      role="option"
                      className={`savings-asp-plan-option ${opt === selectedPlan ? 'is-selected' : ''}`}
                      onClick={() => {
                        onSelectPlan(opt);
                        setPlanMenuOpen(false);
                      }}
                    >
                      {opt}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>

        {showGoalAmount ? (
          <div className="savings-asp-amount-card">
            <div className="savings-asp-amount-top">
              <span className="savings-asp-set-amount-label">Set Amount</span>
              <div className={`savings-asp-wallet-wrap ${tokenMenuOpen ? 'is-open' : ''}`} ref={tokenWrapRef}>
                <button
                  type="button"
                  className={`savings-asp-wallet-pill ${tokenMenuOpen ? 'is-open' : ''}`}
                  aria-haspopup="listbox"
                  aria-expanded={tokenMenuOpen}
                  aria-label="Token"
                  disabled={isSubmitting}
                  onClick={() => {
                    setTokenMenuOpen((open) => !open);
                    setPlanMenuOpen(false);
                    setFreqMenuOpen(false);
                    setDateCloseTick((tick) => tick + 1);
                  }}
                >
                  {showTokenIcon ? (
                    <span className={`savings-asp-wallet-badge is-icon ${String(activeTicker).toUpperCase() === 'XRP' ? 'is-xrp' : ''}`}>
                      <img src={activeIconUrl} alt="" />
                    </span>
                  ) : (
                    <span className="savings-asp-wallet-badge">{activeTicker}</span>
                  )}
                  <span className="savings-asp-wallet-pill-label">{activeLabel}</span>
                  <ChevronDown size={16} strokeWidth={2} className="savings-asp-wallet-chevron" aria-hidden />
                </button>
                {tokenMenuOpen ? (
                  <ul className="savings-asp-wallet-menu" role="listbox" aria-label="Token">
                    {tokens.map((token) => {
                      const selected = token.id === activeToken?.id;
                      const ticker = token.ticker || token.id;
                      const iconUrl = token.iconUrl || (String(ticker).toUpperCase() === 'XRP' ? XRP_IMG : '');
                      return (
                        <li key={token.id}>
                          <button
                            type="button"
                            role="option"
                            aria-selected={selected}
                            className={`savings-asp-wallet-option ${selected ? 'is-selected' : ''}`}
                            onClick={() => {
                              setInternalTokenId(token.id);
                              onSelectToken?.(token.id);
                              setTokenMenuOpen(false);
                            }}
                          >
                            <span className="savings-asp-wallet-option-main">
                              {useCoinIconInPill && iconUrl ? (
                                <span className={`savings-asp-wallet-badge is-icon ${String(ticker).toUpperCase() === 'XRP' ? 'is-xrp' : ''}`}>
                                  <img src={iconUrl} alt="" />
                                </span>
                              ) : (
                                <span className="savings-asp-wallet-badge">{ticker}</span>
                              )}
                              <span>{token.label}</span>
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
              </div>
            </div>
            <div className="savings-asp-amount-hero">
              <input
                type="text"
                className="savings-asp-amount-input"
                placeholder="24,000"
                value={amount}
                onChange={onAmountRowInput}
                inputMode="decimal"
                disabled={isSubmitting}
                aria-label="Savings goal amount"
              />
              <span className="savings-asp-amount-suffix">{activeTicker}</span>
            </div>
            <ExchangeRateLine line={goalExchangeLine} emphasizeValue={showAutoFields} />
          </div>
        ) : null}

        {showTargetDate ? (
          <div className="savings-asp-field">
            <label className="savings-asp-label" htmlFor="savings-asp-target-date">
              Target date
            </label>
            <SavingsTargetDatePicker
              id="savings-asp-target-date"
              value={targetDate}
              onChange={onTargetDateChange}
              disabled={isSubmitting}
              closeTick={dateCloseTick}
              onOpen={() => {
                setPlanMenuOpen(false);
                setFreqMenuOpen(false);
                setTokenMenuOpen(false);
              }}
            />
          </div>
        ) : null}

        {showAutoFields ? (
          <>
            <div className="savings-asp-field">
              <label className="savings-asp-label" htmlFor="savings-asp-autosave-amt">
                AutoSave amount
              </label>
              <input
                id="savings-asp-autosave-amt"
                type="text"
                className="savings-asp-input"
                placeholder="Add"
                value={autoSaveAmount}
                onChange={onAutosaveAmountInput}
                inputMode="decimal"
                disabled={isSubmitting}
                autoComplete="off"
              />
              <ExchangeRateLine
                line={autoSaveExchangeLine}
                emphasizeValue
                className="savings-asp-rate-line--field"
              />
            </div>
            <div className="savings-asp-field">
              <span className="savings-asp-label" id="savings-asp-freq-label">
                Autosave frequency
              </span>
              <div className="savings-asp-menu-wrap" ref={freqWrapRef}>
                <button
                  type="button"
                  className={`savings-asp-trigger ${freqMenuOpen ? 'is-open' : ''}`}
                  aria-expanded={freqMenuOpen}
                  aria-haspopup="listbox"
                  aria-labelledby="savings-asp-freq-label"
                  onClick={() => {
                    setFreqMenuOpen((o) => !o);
                    setPlanMenuOpen(false);
                    setTokenMenuOpen(false);
                    setDateCloseTick((tick) => tick + 1);
                  }}
                  disabled={isSubmitting}
                >
                  <span
                    className={`savings-asp-trigger-value ${
                      !autoSaveFrequency ? 'savings-asp-trigger-value--placeholder' : ''
                    }`}
                  >
                    {autoSaveFrequency || 'Add'}
                  </span>
                  <ChevronDown size={18} strokeWidth={2} className="savings-asp-trigger-chevron" aria-hidden />
                </button>
                {freqMenuOpen ? (
                  <ul className="savings-asp-plan-menu" role="listbox">
                    {autosaveFrequencyOptions.map((opt) => (
                      <li key={opt}>
                        <button
                          type="button"
                          role="option"
                          className={`savings-asp-plan-option ${
                            opt === autoSaveFrequency ? 'is-selected' : ''
                          }`}
                          onClick={() => {
                            onAutoSaveFrequencyChange(opt);
                            setFreqMenuOpen(false);
                          }}
                        >
                          {opt}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </div>
          </>
        ) : null}

        <button type="button" className="savings-asp-submit" onClick={onCreate} disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <LoadingIndicator size="sm" />
              <span>Creating…</span>
            </>
          ) : (
            'Create'
          )}
        </button>

        <div className="savings-asp-footnote">
          <Info size={16} strokeWidth={2} aria-hidden />
          <span>{infoNote}</span>
        </div>
      </div>
    </div>
  );
};

export default AddSavingsPlanModal;
export { DEFAULT_PLANS, DEFAULT_AUTOSAVE_FREQUENCIES, DEFAULT_TOKENS };
